import React from 'react'

/**
 * Sacred-geometry "Flower of Life" decorative SVG.
 * 19 evenly-spaced circles in a hexagonal flower lattice.
 *
 * Replaces the prior PolarGrid + FibonacciSpiral pair in the Verse section.
 */
export function FlowerOfLife({ size = 360, strokeOpacity = 0.18, className = '' }) {
    const r = size / 6           // unit radius — chosen so ring 2 just fits the viewBox
    const cx = size / 2
    const cy = size / 2

    // hexagonal lattice positions, where unit distance between adjacent circle centres = r.
    // Ring 0: centre (1)
    // Ring 1: 6 circles at radius r
    // Ring 2: 12 circles — 6 at radius r√3 (edge midpoints) + 6 at radius 2r (vertices)
    const positions = [
        [0, 0],
    ]

    // Ring 1 — 6 circles at distance r, every 60°, offset by 0°
    for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3
        positions.push([r * Math.cos(a), r * Math.sin(a)])
    }

    // Ring 2 — 6 vertex circles at distance 2r, every 60°
    for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3
        positions.push([2 * r * Math.cos(a), 2 * r * Math.sin(a)])
    }

    // Ring 2 — 6 edge circles at distance r√3, offset by 30°
    for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3 + Math.PI / 6
        positions.push([Math.sqrt(3) * r * Math.cos(a), Math.sqrt(3) * r * Math.sin(a)])
    }

    return (
        <svg
            className={`flower-of-life ${className}`}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            fill="none"
            aria-hidden="true"
        >
            {/* faint outer container ring */}
            <circle
                cx={cx}
                cy={cy}
                r={3 * r}
                stroke="currentColor"
                strokeWidth="0.6"
                strokeOpacity={strokeOpacity * 0.6}
            />
            {positions.map(([dx, dy], i) => (
                <circle
                    key={i}
                    cx={cx + dx}
                    cy={cy + dy}
                    r={r}
                    stroke="currentColor"
                    strokeWidth="0.7"
                    strokeOpacity={strokeOpacity * (i === 0 ? 1.4 : 1)}
                />
            ))}
            {/* small accent dot at the centre */}
            <circle cx={cx} cy={cy} r="2" fill="currentColor" opacity="0.5" />
        </svg>
    )
}
